import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const TOKEN_KEY = "nodera_token";
export const ROLE_KEY = "nodera_role";

// New cookie-based CSRF token — set by the backend at login as a
// non-httpOnly `__Host-csrf_token` cookie. We echo it on writes.
const CSRF_COOKIE_NAME = "__Host-csrf_token";
const CSRF_HEADER = "X-CSRF-Token";

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const prefix = `${name}=`;
  const parts = document.cookie ? document.cookie.split(";") : [];
  for (const raw of parts) {
    const p = raw.trim();
    if (p.startsWith(prefix)) return p.slice(prefix.length);
  }
  return null;
}

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  // Send cookies (auth + CSRF) with every request. Required for the
  // httpOnly-cookie auth transport.
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  // Attach CSRF header on state-changing requests (double-submit pattern).
  const method = (config.method || "get").toUpperCase();
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    const csrf = readCookie(CSRF_COOKIE_NAME);
    if (csrf) {
      config.headers = config.headers || {};
      config.headers[CSRF_HEADER] = csrf;
    }
  }
  // Legacy bearer fallback: during the staged migration, keep sending the
  // Authorization header from localStorage/sessionStorage so pages that
  // were pre-cookie continue to authenticate. Once the cookie is set on
  // first login, backend prefers cookie transport.
  const token = sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response && err.response.status === 401) {
      const role = sessionStorage.getItem(ROLE_KEY) || localStorage.getItem(ROLE_KEY);
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(ROLE_KEY);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(ROLE_KEY);
      // Only bounce to login from protected areas. Public marketing pages
      // (blog, pricing, home…) fire an anonymous /auth/me on load that 401s
      // by design — never redirect those visitors.
      if (typeof window !== "undefined") {
        const path = window.location.pathname;
        const isProtected =
          path.startsWith("/portal") ||
          path.startsWith("/admin/") ||
          (path.startsWith("/vendor") && path !== "/vendor/login" && !path.startsWith("/vendors"));
        if (isProtected) {
          const redirect =
            role === "admin" || path.startsWith("/admin/") ? "/admin" : "/login";
          window.location.assign(redirect);
        }
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  return String(detail);
}

/**
 * Resolve a stored asset URL for rendering. Backend returns relative paths
 * like "/api/static/vendor-logos/abc.png" — prefix them with the public backend URL.
 * Pass-through for empty/external URLs.
 */
export function resolveAssetUrl(url) {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/")) return `${BACKEND_URL}${url}`;
  return url;
}
